/**
 * useShortcuts - React hook for handling global keyboard shortcuts.
 *
 * Allows registering handlers for common app-wide shortcuts (Cmd/Ctrl+K, N, etc) and custom keys.
 * Used for navigation, sidebar toggling, and productivity features.
 */
import { useEffect } from 'react';

interface ShortcutsOptions {
  onCommandK?: () => void;
  onCommandSlash?: () => void;
  onCommandShift1?: () => void;
  onCommandShift2?: () => void;
  onCommandShiftL?: () => void;
  onCommand1?: () => void;
  onCommand2?: () => void;
  onCommand3?: () => void;
  additionalShortcuts?: Record<string, () => void>;
}

export const useShortcuts = ({
  onCommandK,
  onCommandSlash,
  onCommandShift1,
  onCommandShift2,
  onCommandShiftL,
  onCommand1,
  onCommand2,
  onCommand3,
  additionalShortcuts
}: ShortcutsOptions) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle Command+K (Mac) or Ctrl+K (Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onCommandK?.();
      }

      // Handle Command+\ (Mac) or Ctrl+\ (Windows) for sidebar toggle
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault();
        onCommandSlash?.();
      }

      // Handle Command+Shift+1 (Mac) or Ctrl+Shift+1 (Windows) for new course
      if ((e.metaKey || e.ctrlKey) && e.key === '1' && e.shiftKey) {
        e.preventDefault();
        onCommandShift1?.();
      }

      // Handle Command+Shift+2 (Mac) or Ctrl+Shift+2 (Windows) for new note
      if ((e.metaKey || e.ctrlKey) && e.key === '2' && e.shiftKey) {
        e.preventDefault();
        onCommandShift2?.();
      }

      // Handle Command+Shift+L (Mac) or Ctrl+Shift+L (Windows) for theme toggle
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'l' && e.shiftKey) {
        e.preventDefault();
        onCommandShiftL?.();
      }

      // Handle Command+1 (Mac) or Ctrl+1 (Windows) for flashcard panel
      if ((e.metaKey || e.ctrlKey) && e.key === '1') {
        e.preventDefault();
        onCommand1?.();
      }

      // Handle Command+2 (Mac) or Ctrl+2 (Windows) for summary panel
      if ((e.metaKey || e.ctrlKey) && e.key === '2') {
        e.preventDefault();
        onCommand2?.();
      }

      // Handle Command+3 (Mac) or Ctrl+3 (Windows) for chat panel
      if ((e.metaKey || e.ctrlKey) && e.key === '3') {
        e.preventDefault();
        onCommand3?.();
      }

      // Handle additional shortcuts
      if (additionalShortcuts) {
        const shortcut = additionalShortcuts[e.key.toLowerCase()];
        if (shortcut) {
          e.preventDefault();
          shortcut();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCommandK, onCommandSlash, onCommandShift1, onCommandShift2, onCommandShiftL, onCommand1, onCommand2, onCommand3, additionalShortcuts]);
}; 